import React from 'react';
import { useTranslation } from 'react-i18next';
import ClinicFilterSelectBoxV2 from 'components/molecules/include-clinic-select-box';
import DoctorSelectBox from 'components/molecules/doctor-select-box';
import ExpandableFilterPanel, {
  ExpandableFilterPanelItem,
} from 'components/molecules/expandable-filter-panel';
import MonthPicker from 'components/molecules/datepicker/monthPicker';
import useUser from 'utils/useUser';
import { Roles } from 'infrastructure/enums';

import type { IMonthSelection } from './types';

interface IProviderDashboardFiltersProps {
  selectedMonth: IMonthSelection;
  onMonthChange: (value: IMonthSelection | null) => void;
  clinicGuid: string[];
  parentClinicGuid: string[];
  onClinicChange: (props: {
    included?: string[];
    parentClinicGuid?: string[];
  }) => void;
  filterCount: number;
  providerGuid?: string;
  onProviderChange: (value: string | undefined) => void;
  onReset: () => void;
  /** CSS module class names from the page */
  styles: Record<string, string>;
}

export const ProviderDashboardFilters: React.FC<
  IProviderDashboardFiltersProps
> = ({
  selectedMonth,
  onMonthChange,
  clinicGuid: included,
  parentClinicGuid,
  onClinicChange,
  filterCount,
  providerGuid,
  onProviderChange,
  onReset,
  styles: s,
}) => {
  const { t } = useTranslation();
  const { hasAnyRole } = useUser();
  const isAccountManager = hasAnyRole([Roles.AccountManager]);
  const dateRangeSelect = (
    <div className={s.dateRangeWithIcon}>
      <MonthPicker
        value={selectedMonth}
        onChange={(value) => {
          if (!value) {
            // If cleared, default to current month
            const now = new Date();
            onMonthChange({
              year: now.getFullYear(),
              month: now.getMonth() + 1,
            });
          } else {
            onMonthChange(value);
          }
        }}
        allowClear={false}
        maxDate={new Date()}
      />
    </div>
  );

  const clinicSelect = (
    <ClinicFilterSelectBoxV2
      useAdminApi
      showOnInteractiveTimeDashboard={false}
      included={included}
      parentClinicGuid={parentClinicGuid}
      onChange={onClinicChange}
      withoutLabel
    />
  );

  return (
    <ExpandableFilterPanel
      title={t('controls.filters')}
      filtersCount={filterCount}
      onReset={onReset}
      openWithValue
      isExpanded
    >
      <div className={s.filtersForm}>
        <ExpandableFilterPanelItem borderBottom label={t('labels.dateRange')}>
          {dateRangeSelect}
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem borderBottom label={t('labels.clinics')}>
          {clinicSelect}
        </ExpandableFilterPanelItem>
        {isAccountManager && (
          <ExpandableFilterPanelItem label={t('labels.provider')}>
            <DoctorSelectBox
              allowClear
              value={providerGuid}
              clinicGuid={included}
              onChange={(value) => onProviderChange(value || undefined)}
            />
          </ExpandableFilterPanelItem>
        )}
      </div>
    </ExpandableFilterPanel>
  );
};
